<template>
    <div class="div-principal">
        <div v-if="loadingScreen" class="loadingScreen">
            <h1>Processando Requisição , aguarde!</h1>
            <v-progress-circular class="loadingScreen" indeterminate :size="50" color="purple"></v-progress-circular>
        </div>

        <div v-if="erpDialog" class="loadingScreen">
            <erp-dialog :alert-type="erpAlertType" :alert-message="erpResponse"></erp-dialog>
        </div>

        <v-container v-if="bookingWasLoaded" class="container-detail">
            <v-card class="booking-detail-card">

                <!-- HEADER -->
                <div class="booking-header">
                    <div class="booking-header-toolbar">
                        <v-btn color="grey lighten-1" class="rounded-lg" outlined x-small @click="$router.go(-1)">
                            <v-icon left small>mdi-arrow-left</v-icon>
                            Voltar
                        </v-btn>
                        <div class="assignment-panel assignment-toolbar-panel">
                            <booking-assignment-card :booking="booking" @updated="reloadBooking" />
                        </div>
                    </div>
                </div>

                <template v-if="requiresAssignment">
                    <div class="lock-placeholder">
                        <v-icon x-large color="grey lighten-1">mdi-lock-outline</v-icon>
                        <strong class="subtitle-1 grey--text text--darken-2">Atribua esta reserva a um responsável.</strong>
                        <span class="body-2 grey--text text--darken-1 text-center">As informações e as ações disponíveis ficarão visíveis somente após a atribuição.</span>
                        <v-btn
                            color="primary"
                            :loading="loadingAssignToMe"
                            class="mt-2"
                            @click="assignToMe"
                        >
                            <v-icon left small>mdi-account-check-outline</v-icon>
                            Atribuir a mim
                        </v-btn>
                    </div>
                </template>
                <template v-else>

                <!-- RESUMO -->
                <div class="page-section mb-8">
                    <div class="section-title">
                        <v-icon left small color="#bdbdbd">mdi-text-box-outline</v-icon>
                        Resumo
                    </div>
                    <v-row>
                        <v-col cols="12" :md="booking.status === 'canceled' ? 8 : 12">
                            <v-card outlined class="fare-card">
                                <v-card-text>
                                    <v-row dense class="mt-2 text-center">
                                        <v-col cols="4">
                                            <span class="info-label">Protocolo Belchior</span>
                                            <strong class="info-value">{{ booking.id }}</strong>
                                        </v-col>
                                        <v-col cols="4">
                                            <span class="info-label">Protocolo Onfly</span>
                                            <strong class="info-value">{{ booking.order_id_system }}</strong>
                                        </v-col>
                                        <v-col cols="4">
                                            <span class="info-label">Status</span>
                                            <v-chip
                                                small
                                                class="status-chip"
                                                :class="`status-chip--${booking.status}`"
                                            >
                                                {{ getStatus }}
                                            </v-chip>
                                        </v-col>
                                    </v-row>
                                    <v-divider class="my-4" />
                                    <v-row dense class="mt-2 text-center">
                                        <v-col cols="4">
                                            <span class="info-label">Check-in</span>
                                            <div class="info-value">{{ booking.check_in }}</div>
                                        </v-col>
                                        <v-col cols="4">
                                            <span class="info-label">Check-out</span>
                                            <div class="info-value">{{ booking.check_out }}</div>
                                        </v-col>
                                        <v-col cols="4">
                                            <span class="info-label">Diárias</span>
                                            <div class="info-value">{{ booking.total_stay_nights }}</div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col v-if="booking.status === 'canceled'" cols="12" md="4">
                            <v-card outlined class="fare-card">
                                <v-card-text class="canceled-reason">
                                    <strong>Motivo do cancelamento:</strong>
                                    {{ booking.canceled_reason ?? 'N/A' }}
                                    <cancellation-recurrence-alert :booking="booking" />
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <!-- HOTEL -->
                <div v-if="booking.hotel" class="page-section mb-8">
                    <div class="section-title">
                        <v-icon left small color="#bdbdbd">mdi-office-building</v-icon>
                        Hotel
                    </div>
                    <v-row no-gutters class="hotel-block">
                        <v-col cols="12" md="4">
                            <v-img
                                :src="`/storage/hotels${booking.hotel.thumbnail}`"
                                class="hotel-section-img"
                                contain
                            />
                        </v-col>
                        <v-col cols="12" md="8" class="pl-8 pt-4 pr-4 pb-4">
                            <div class="hotel-name-row mb-1">
                                <div class="hotel-name">{{ booking.hotel.name }}</div>
                                <v-btn text x-small color="grey" :to="`/edit-hotel/${booking.hotel.id}`" class="hotel-edit-btn">
                                    <v-icon x-small left>mdi-open-in-new</v-icon>
                                    Ver cadastro
                                </v-btn>
                            </div>
                            <p class="hotel-address mb-4">
                                {{ booking.hotel.address }}, {{ booking.hotel.district }} — {{ booking.hotel.city }}, {{ booking.hotel.state }}
                            </p>
                            <v-row dense class="mt-3">
                                <v-col cols="6" class="mb-3">
                                    <span class="info-label">CNPJ</span>
                                    <div class="info-value">{{ booking.hotel.cnpj }}</div>
                                </v-col>
                                <v-col cols="6" class="mb-3">
                                    <span class="info-label">Email</span>
                                    <div class="info-value">{{ booking.hotel.email }}</div>
                                </v-col>
                                <v-col cols="6" class="mb-3">
                                    <span class="info-label">Telefone</span>
                                    <div class="info-value">{{ booking.hotel.telephone }}</div>
                                </v-col>
                                <v-col v-if="booking.hotel.secondary_email" cols="6" class="mb-3">
                                    <span class="info-label">Email secundário</span>
                                    <div class="info-value">{{ booking.hotel.secondary_email }}</div>
                                </v-col>
                                <v-col v-if="booking.hotel.secondary_telephone" cols="6" class="mb-3">
                                    <span class="info-label">Telefone secundário</span>
                                    <div class="info-value">{{ booking.hotel.secondary_telephone }}</div>
                                </v-col>
                                <v-col v-if="booking.hotel.brand" cols="6" class="mb-3">
                                    <span class="info-label">Marca</span>
                                    <div class="info-value">{{ booking.hotel.brand }}</div>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </div>

                <!-- PAGAMENTO + NOTAS -->
                <v-row class="mb-8">
                    <v-col cols="12" md="7">
                        <div class="page-section">
                            <div class="section-title">
                                <v-icon left small color="#bdbdbd">mdi-cash</v-icon>
                                Pagamento
                            </div>
                            <v-card outlined class="fare-card">
                                <booking-payment :booking="booking"/>
                            </v-card>
                        </div>
                    </v-col>
                    <v-col cols="12" md="5">
                        <div class="page-section">
                            <div class="section-title">
                                <v-icon left small color="#bdbdbd">mdi-note-text</v-icon>
                                Notas
                            </div>
                            <v-card outlined class="fare-card">
                                <v-card-text>
                                    <v-textarea
                                        v-model="booking_note"
                                        name="input-7-4"
                                        label="Digite sua nota"
                                        hide-details
                                        rows="3"
                                        auto-grow
                                    />
                                    <v-btn small outlined color="primary" class="mt-4" @click="sendNote(booking.id)">
                                        Salvar Nota
                                        <v-icon small color="primary" class="pl-2">mdi-note-text</v-icon>
                                    </v-btn>
                                </v-card-text>
                            </v-card>
                        </div>
                    </v-col>
                </v-row>

                <!-- ACOMODAÇÕES -->
                <div class="page-section mb-8">
                    <v-row>
                        <v-col cols="12" md="7">
                            <div class="section-title">
                                <v-icon left small color="#bdbdbd">mdi-bed</v-icon>
                                Acomodações
                            </div>
                            <v-card
                                v-for="(room, roomIndex) in booking.rooms"
                                :key="room.id || roomIndex"
                                outlined
                                class="fare-card mb-3"
                            >
                                <v-card-title class="fare-card-title subtitle-1 font-weight-bold py-3 px-4">
                                    {{ room.name || 'Acomodação ' + (roomIndex + 1) }}
                                </v-card-title>
                                <v-divider />
                                <v-card-text>
                                    <v-row dense class="mb-2" align="center">
                                        <v-col cols="auto" class="pr-6">
                                            <span class="info-label">Tarifa diária</span>
                                            <div class="info-value">R$ {{ formatAmount(room.fare.amount) }}</div>
                                        </v-col>
                                        <v-col cols="auto" class="pr-6">
                                            <span class="info-label">Diárias</span>
                                            <div class="info-value">{{ booking.numberOfNights }}</div>
                                        </v-col>
                                        <v-col cols="auto" class="pr-6">
                                            <span class="info-label">Viajantes</span>
                                            <div class="info-value">{{ room.booking_guests.length }}</div>
                                        </v-col>
                                        <v-col cols="auto" class="ml-auto">
                                            <v-tooltip v-if="room.fare && room.fare.is_combined" bottom max-width="280">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-chip small color="primary" v-bind="attrs" v-on="on">
                                                        Tarifa combinada
                                                        <v-icon x-small right>mdi-information-outline</v-icon>
                                                    </v-chip>
                                                </template>
                                                <span>Este quarto possui uma tarifa combinada. Como as diárias têm valores diferentes, o preço exibido é a média entre todos os dias da estadia.</span>
                                            </v-tooltip>
                                        </v-col>
                                    </v-row>
                                    <v-divider class="mb-3" />
                                    <div class="info-label mb-2">Hóspedes</div>
                                    <v-row dense>
                                        <v-col v-for="guest in room.booking_guests" :key="guest.id" cols="12" sm="6">
                                            <div class="guest-item pa-2">
                                                <div class="guest-name">{{ guest.name }}</div>
                                                <div class="guest-doc">{{ guest.document }}</div>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12" md="5">
                            <div class="section-title">
                                <v-icon left small color="#bdbdbd">mdi-history</v-icon>
                                Histórico
                            </div>
                            <v-card outlined class="fare-card">
                                <v-card-text class="pl-0">
                                    <StatusReserve :booking="booking"/>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </div>

                <!-- AÇÕES -->
                <div class="page-section">
                    <div class="section-title">
                        <v-icon left small color="#bdbdbd">mdi-lightning-bolt</v-icon>
                        Ações
                    </div>
                    <div class="actions-row">

                        <v-dialog
                            v-if="booking.histories.length === 5 && booking.invoice === null && booking.histories[4].status === 'invoiceReminderSent'"
                            v-model="dialogUpload"
                            persistent
                            max-width="400"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn color="primary" dark v-bind="attrs" v-on="on">Upload de nota fiscal</v-btn>
                            </template>
                            <v-card>
                                <v-card-title class="headline">Submeta o arquivo (PDF)</v-card-title>
                                <v-file-input
                                    class="pl-3 pr-3"
                                    v-model="invoice"
                                    :rules="rules"
                                    accept=".pdf"
                                    placeholder="Selecione o arquivo em pdf"
                                    prepend-icon="mdi-camera"
                                    outlined
                                />
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn color="green darken-1" text @click="dialogUpload = false">voltar</v-btn>
                                    <v-btn color="primary" text @click="uploadInvoice(booking.id)">Anexar</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <v-dialog
                            v-if="booking.status === 'pending' && $can('confirm.cancel-booking')"
                            v-model="cancelBookingDialog"
                            persistent
                            max-width="440"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn color="#FF3D00" dark v-bind="attrs" v-on="on">Cancelar Reserva</v-btn>
                            </template>
                            <v-card>
                                <v-card-title class="headline">Qual o motivo do Cancelamento?</v-card-title>
                                <v-textarea v-model="canceledReason" class="pl-3 pr-3" solo name="input-7-4" label="digite aqui o motivo..." />
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn color="darken-1" text @click="[cancelBookingDialog = false, canceledReason = '']">voltar</v-btn>
                                    <v-btn color="error" :disabled="loadingConfirmed" @click="cancelBooking(booking.id)">Cancelar Reserva</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <v-dialog
                            v-if="booking.status === 'pending' && $can('confirm.cancel-booking')"
                            v-model="dialogConfirmed"
                            persistent
                            max-width="440"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn color="green darken-1" dark v-bind="attrs" v-on="on">Confirmar Reserva</v-btn>
                            </template>
                            <v-card>
                                <v-card-title class="headline">Confirmar Reserva?</v-card-title>
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn color="darken-1" text @click="dialogConfirmed = false">voltar</v-btn>
                                    <v-btn color="success" :loading="loadingConfirmed" @click="confirmedBooking(booking.id)">Confirmar</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <v-btn v-if="booking.invoice !== null" color="primary" @click="downloadInvoice(booking.id)">
                            Baixar nota fiscal
                        </v-btn>

                        <v-btn v-if="booking.maySendInvoiceEmail" color="primary" @click="retryEmailInvoice(booking.id)">
                            Reenviar Nota fiscal
                        </v-btn>

                        <v-btn v-if="cardHasToBeCreated()" :loading="loadingRetryVcn" color="primary" @click="retryVcn(booking.id)">
                            Regerar Cartão
                        </v-btn>

                        <v-btn v-if="cardHasToBeRecharged" :loading="loadingRechargeVcn" color="primary" @click="retryRechargeVcn(booking.id)">
                            Recarregar Cartão
                        </v-btn>

                        <v-btn v-if="canUnblockCard" :loading="loadingUnblockVcn" color="primary" @click="retryUnblockVcn(booking.id)">
                            Desbloquear Cartão
                        </v-btn>

                        <v-btn v-if="!booking.integrated_with_erp && booking.invoice !== null" color="green" @click="integrateWithErp(booking.id)">
                            Integrar com ERP
                        </v-btn>

                        <retry-pix-payment :booking="booking" />

                        <v-dialog v-if="canResendSolicitationEmail" v-model="resendEmailDialog" persistent max-width="550">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-if="$can('resend.notifications')" color="primary" dark v-bind="attrs" v-on="on">
                                    Reenviar solicitação
                                </v-btn>
                            </template>
                            <v-card width="550">
                                <v-card-title class="headline">
                                    <p class="pa-0">Para qual email deseja reenviar a reserva?</p>
                                </v-card-title>
                                <v-card-text class="pb-0">
                                    <v-row>
                                        <v-col cols="12" class="pa-0">
                                            <v-checkbox v-model="registeredEmails" label="Email cadastrado" />
                                        </v-col>
                                        <v-col cols="12" class="pa-0 mb-2">
                                            <p>{{ booking.hotel.email }}</p>
                                        </v-col>
                                    </v-row>
                                    <v-divider />
                                    <v-row>
                                        <v-col cols="12" class="pa-0 mt-2">
                                            <v-checkbox v-model="alternativeEmailSelected" label="Email Alternativo" />
                                        </v-col>
                                        <v-col cols="12" class="pa-0">
                                            <v-text-field
                                                v-model="alternativeEmail"
                                                :disabled="!alternativeEmailSelected"
                                                :rules="emailRules"
                                                ref="alternativeEmailInput"
                                                label="Email alternativo"
                                                class="px-2"
                                                outlined
                                            />
                                        </v-col>
                                    </v-row>
                                    <p class="email-error" v-if="!oneEmailOptionIsSelected">*Pelo menos um campo deve ser selecionado!</p>
                                </v-card-text>
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn color="darken-1" text :disabled="resendEmailBtnLoading" @click="closeResendEmailDialog">voltar</v-btn>
                                    <v-btn color="success" :loading="resendEmailBtnLoading" @click="resendBookingEmail(booking.id)">Reenviar</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <v-dialog v-if="canResendPaymentEmail" v-model="resendConfirmEmailDialog" persistent max-width="570">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn color="primary" dark v-bind="attrs" v-on="on">{{ resendPaymentLabel }}</v-btn>
                            </template>
                            <v-card width="570">
                                <v-card-title class="headline">
                                    <p class="pa-0">Para qual email deseja reenviar o pagamento?</p>
                                </v-card-title>
                                <v-card-text class="pb-0">
                                    <v-row>
                                        <v-col cols="12" class="pa-0">
                                            <v-checkbox v-model="registeredEmails" label="Email cadastrado" />
                                        </v-col>
                                        <v-col cols="12" class="pa-0 mb-2">
                                            <p>{{ booking.hotel.email }}</p>
                                        </v-col>
                                    </v-row>
                                    <v-divider />
                                    <v-row>
                                        <v-col cols="12" class="pa-0 mt-2">
                                            <v-checkbox v-model="alternativeEmailSelected" label="Email Alternativo" />
                                        </v-col>
                                        <v-col cols="12" class="pa-0">
                                            <v-text-field
                                                v-model="alternativeEmail"
                                                :disabled="!alternativeEmailSelected"
                                                :rules="emailRules"
                                                ref="alternativeEmailInput"
                                                label="Email alternativo"
                                                class="px-2"
                                                outlined
                                            />
                                        </v-col>
                                    </v-row>
                                    <p class="email-error" v-if="!oneEmailOptionIsSelected">*Pelo menos um campo deve ser selecionado!</p>
                                </v-card-text>
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn color="darken-1" text :disabled="resendEmailBtnLoading" @click="closeResendEmailDialog">voltar</v-btn>
                                    <v-btn color="success" :loading="resendEmailBtnLoading" @click="resendPaymentEmail(booking.id)">Reenviar</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <create-blue-card v-if="canCreateBlueCard" :booking-id="booking.id" />

                    </div>
                </div>

                </template>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import PaymentCard from "../PaymentCard.vue";
import StatusReserve from "../StatusReserve.vue";
import ErpDialog from "../ErpDialog.vue";
import RetryPixPayment from "./Actions/RetryPixPayment.vue";
import ChangeBookingFare from "./Actions/ChangeBookingFare.vue";
import ChangePixKey from "./Actions/ChangePixKey.vue";
import ShowPixReceipt from "./Actions/ShowPixReceipt.vue";
import BookingPayment from "./BookingPayment.vue";
import CreateBlueCard from "./Actions/CreateBlueCard.vue";
import CancellationRecurrenceAlert from "../../components/Reserve/CancellationRecurrenceAlert.vue";
import BookingAssignmentCard from "../../components/Reserve/BookingAssignmentCard.vue";

export default {
    name: "DetailReserve",
    components: {
        CreateBlueCard,
        BookingPayment,
        ChangeBookingFare,
        ShowPixReceipt,
        ChangePixKey,
        RetryPixPayment,
        PaymentCard,
        StatusReserve,
        ErpDialog,
        CancellationRecurrenceAlert,
        BookingAssignmentCard,
    },
    data: () => ({
        loadingScreen: false,
        booking: null,
        resendEmailDialog: false,
        resendConfirmEmailDialog: false,
        cancelBookingDialog: false,
        pixPaidDialog: false,
        dialogUpload: false,
        dialogConfirmed: false,
        cardDialog: false,
        erpDialog: false,
        erpAlertType: "success",
        erpResponse: "",
        canceledReason: '',
        booking_note: '',
        emittedByOperator: 1,
        invoice: null,
        loading: true,
        registeredEmails: true,
        alternativeEmailSelected: false,
        alternativeEmail: '',
        emailNew: '',
        resendEmailBtnLoading: false,
        loadingConfirmed: false,
        loadingRetryVcn: false,
        loadingRechargeVcn: false,
        loadingUnblockVcn: false,
        loadingPendingPayment: false,
        loadingAssignToMe: false,
    }),
    computed: {
        emailRules() {
            if (this.alternativeEmailSelected) {
                return [
                    v => !!v || 'O email é obrigatorio',
                    v => !!v.match('^(([^<>()[\\]\\\\.,;:\\s@"]+(\\.[^<>()[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$') || 'O email é inválido'
                ];
            }
        },
        cardHasToBeRecharged() {
            return this.booking.payment.card_id !== null && this.booking.payment.amount !== this.booking.total_value;
        },
        getStatus() {
            if (this.booking.status === "canceled") return "Cancelado";
            if (this.booking.status === "confirmed") return "Confirmado";
            if (this.booking.status === "pendingPayment") return "Pagamento pendente";
            if (this.booking.status === "pending") return "Pendente";
            if (this.booking.status === "unanswered") return "Não respondido";
            if (this.booking.status === "unpaid") return "Pagamento não efetuado";
        },
        oneEmailOptionIsSelected() {
            return this.alternativeEmailSelected || this.registeredEmails;
        },
        canResendSolicitationEmail() {
            return this.booking.status === 'pending';
        },
        canResendPaymentEmail() {
            if (this.booking.payment.payment_type === 'virtualCard' && this.booking.payment.card_id !== null) {
                return true;
            } else if (
                this.booking.status === 'confirmed'
                && this.booking.payment.payment_type === 'pix'
                && this.booking.payment.pix_key !== null
            ) {
                return true;
            } else if (this.booking.payment_type === 'invoicing') {
                return true;
            }
            return false;
        },
        resendPaymentLabel() {
            if (this.booking.payment.payment_type === 'pix') return 'Reenviar comprovante';
            return 'Reenviar confirmação';
        },
        canUnblockCard() {
            const today = new Date(this.getTodayDate()).toUTCString();
            const check_in = new Date(this.formatCheckinDate(this.booking.check_in)).toUTCString();
            const created_at = new Date(this.formatCreatedAtDate(this.booking.created_at)).toUTCString();
            return this.booking.hotel.payment_type === 'virtualCard'
                && check_in === today
                && created_at === today
                && this.booking.payment.card_status === 'blocked';
        },
        canCreateBlueCard() {
            return this.booking.status === 'confirmed'
                && this.booking.payment.payment_type === 'virtualCard'
                && this.booking.payment.card_id !== null
                && this.booking.payment.card_provider === 2;
        },
        bookingWasLoaded () {
            return this.booking !== null;
        },
        requiresAssignment() {
            return this.booking !== null && this.booking.status === 'pending' && !this.booking.current_assignee;
        }
    },
    mounted() {
        axios.get(`/api/booking/${this.$route.params.id}`, {
            params: { include_current_assignee: 1 }
        }).then(res => {
            this.booking = res.data;
            this.booking_note = res.data.booking_note;
        });
    },
    methods: {
        uploadInvoice(id) {
            window.confirm('Nota fiscal anexada ');
            const i = new FormData();
            i.append('invoice', this.invoice);
            axios.post(`/api/invoice/${id}`, i).then(() => {
                window.location.reload();
                this.dialogUpload = false;
                this.$forceUpdate();
            });
            window.location.reload();
            this.dialogUpload = false;
        },
        retryEmailInvoice(id) {
            axios.post(`/api/retry/invoice-notification`, { id: id }).then(() => {
                window.confirm('Email reenviado com sucesso');
                window.location.reload();
            });
        },
        cancelBooking(id) {
            this.loadingConfirmed = true;
            if (this.canceledReason === '') {
                window.confirm('O campo é obrigatório ');
                this.loadingConfirmed = false;
            } else {
                axios.put(`/api/booking/${id}`, { canceledReason: this.canceledReason, emittedByOperator: this.emittedByOperator })
                    .then(res => {
                        window.confirm(res.data.message);
                        if (res.data.success) {
                            this.cancelBookingDialog = false;
                            this.$forceUpdate();
                            window.location.reload();
                        }
                    }).catch(() => {
                        window.confirm('Algo deu errado, tente novamente mais tarde');
                        this.loadingConfirmed = false;
                    });
            }
        },
        resendBookingEmail(id) {
            if (this.$refs.alternativeEmailInput.valid && this.oneEmailOptionIsSelected) {
                this.resendEmailBtnLoading = true;
                let data = this.setResendEmailData(id);
                axios.post(`/api/retry/booking-email`, data)
                    .then((res) => {
                        window.confirm(res.data.message);
                        this.resendEmailBtnLoading = false;
                        if (res.data.success) window.location.reload();
                    }).catch(() => {
                        this.resendEmailBtnLoading = false;
                        window.confirm('Erro ao tentar reenviar o e-mail! Atualize a página e tente novamente.');
                    });
            }
        },
        resendPaymentEmail(id) {
            if (this.$refs.alternativeEmailInput.valid && this.oneEmailOptionIsSelected) {
                this.resendEmailBtnLoading = true;
                let data = this.setResendEmailData(id);
                axios.post(`/api/retry/payment-email`, data)
                    .then((res) => {
                        window.confirm(res.data.message);
                        this.resendEmailBtnLoading = false;
                        if (res.data.success) window.location.reload();
                    }).catch(() => {
                        this.resendEmailBtnLoading = false;
                        window.confirm('Erro ao tentar reenviar o e-mail! Atualize a página e tente novamente.');
                    });
            }
        },
        setResendEmailData(id) {
            return {
                id: id,
                isAlternativeEmail: this.alternativeEmailSelected,
                alternativeEmail: this.alternativeEmail,
                isRegisteredEmails: this.registeredEmails,
            };
        },
        sendNote(id) {
            if (!this.booking_note) {
                window.confirm('O campo é obrigatório ');
            } else {
                axios.post(`/api/booking/store-note`, { id: id, booking_note: this.booking_note }).then(() => {
                    window.confirm('Sua nota foi adicionada');
                    window.location.reload();
                });
            }
        },
        retryVcn(id) {
            this.loadingRetryVcn = true;
            axios.post(`/api/retry/vcn`, { id: id })
                .then(res => {
                    if (res.data.success === false) {
                        window.confirm('Atenção: Vcn já criado para esta reserva! ');
                    } else {
                        window.confirm('Vcn sendo processado ');
                    }
                    this.loadingRetryVcn = false;
                    window.location.reload();
                }).catch((err) => {
                    window.confirm('Erro ao criar o cartão! ' + err.message);
                    this.loadingRetryVcn = false;
                    window.location.reload();
                });
        },
        retryRechargeVcn(id) {
            this.loadingRechargeVcn = true;
            axios.post(`/api/retry/recharge-vcn`, { id: id })
                .then(res => {
                    window.confirm(res.data.message);
                    this.loadingRechargeVcn = false;
                    window.location.reload();
                }).catch((err) => {
                    window.confirm(err.data.message);
                    this.loadingRechargeVcn = false;
                    window.location.reload();
                });
        },
        retryUnblockVcn(id) {
            this.loadingUnblockVcn = true;
            axios.post(`/api/retry/unblock-vcn`, { id: id })
                .then(res => {
                    window.confirm(res.data.message);
                    this.loadingRechargeVcn = false;
                    window.location.reload();
                }).catch((err) => {
                    window.confirm(err.data.message);
                    this.loadingRechargeVcn = false;
                    window.location.reload();
                });
        },
        confirmedBooking(id) {
            this.loadingConfirmed = true;
            axios.put(`/api/booking/${id}`, { emittedByOperator: this.emittedByOperator })
                .then(res => {
                    window.confirm(res.data.message);
                    if (res.data.success) {
                        this.cancelBookingDialog = false;
                        this.$forceUpdate();
                        window.location.reload();
                    }
                }).catch(() => {
                    window.confirm('Algo deu errado, tente novamente mais tarde');
                    this.loadingConfirmed = false;
                });
        },
        downloadInvoice(id) {
            if (this.booking.invoice === null) {
                window.confirm('Este hotel não tem nota fiscal anexada ');
            } else {
                window.open(`/api/invoice/download/${this.$route.params.id}`, "_blank");
            }
        },
        integrateWithErp(id) {
            if (!this.booking.integrated_with_erp) {
                this.loadingScreen = true;
                axios.post('/api/invoice/integrate', { id: id }).then(response => {
                    this.loadingScreen = false;
                    this.erpAlertType = response.data.success ? "success" : "error";
                    this.erpResponse = response.data.message;
                    this.erpDialog = true;
                });
            }
        },
        closeResendEmailDialog() {
            this.alternativeEmailSelected = false;
            this.registeredEmails = true;
            this.resendEmailDialog = false;
            this.resendConfirmEmailDialog = false;
        },
        async assignToMe() {
            try {
                this.loadingAssignToMe = true;
                await axios.post(`/api/booking/${this.$route.params.id}/assignment`, { user_id: null });
                this.$simpleDialog('Atribuído a você.', 'success');
                this.reloadBooking();
            } catch (e) {
                this.$simpleDialog('Erro ao atribuir responsável.', 'error');
            } finally {
                this.loadingAssignToMe = false;
            }
        },
        reloadBooking() {
            axios.get(`/api/booking/${this.$route.params.id}`, {
                params: { include_current_assignee: 1 }
            }).then(res => {
                this.booking = res.data;
            });
        },
        cardHasToBeCreated() {
            return this.booking.payment.payment_type === 'virtualCard'
                && this.booking.status === 'confirmed'
                && this.booking.payment.card_id === null;
        },
        formatAmount(cents) {
            return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatCheckinDate(date) {
            const dateArray = date.split('/');
            return `${dateArray[2]}-${dateArray[1]}-${dateArray[0]}`;
        },
        formatCreatedAtDate(date) {
            return date.split('T')[0];
        },
        getTodayDate() {
            const today = new Date();
            const year = today.getFullYear();
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const day = today.getDate().toString().padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
    },
    watch: {
        alternativeEmailSelected: function (val) {
            if (!val) this.alternativeEmail = '';
        },
    }
}
</script>

<style lang="scss" scoped>
.div-principal {
    background-color: #f0f0f0;
    min-height: 100%;
    width: 100%;
}

.container-detail {
    padding: 28px 24px;
}

.booking-detail-card {
    border-radius: 1rem !important;
    padding: 0 24px 36px;
    overflow: hidden;
}

/* ── Header ── */
.booking-header {
    padding: 1rem 0;
    background-color: #fff;
}

.booking-header-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 10px;
}

.assignment-panel { padding: 0; }

.assignment-toolbar-panel {
    width: 450px;
    max-width: 100%;
}

.booking-overview {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
}

.overview-item {
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
}

.overview-label {
    font-size: 10px;
    color: #9e9e9e;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    line-height: 1.4;
}

.overview-item strong {
    display: block;
    font-size: 16px;
    font-weight: 700;
    color: #1f2933;
    white-space: nowrap;
}

.overview-item .protocol-value {
    font-size: 15px;
    font-weight: 600;
    overflow-wrap: anywhere;
    white-space: normal;
}

.overview-status { align-items: flex-start; }

.status-chip {
    font-size: 0.75rem !important;
    font-weight: 600;
}

.status-chip--confirmed      { background-color: rgba(76, 175, 80, 0.15)   !important; color: #2e7d32 !important; }
.status-chip--canceled       { background-color: rgba(244, 67, 54, 0.15)   !important; color: #b71c1c !important; }
.status-chip--pending        { background-color: rgba(251, 192, 45, 0.15)  !important; color: #f57f17 !important; }
.status-chip--pendingPayment { background-color: rgba(255, 152, 0, 0.15)   !important; color: #e65100 !important; }
.status-chip--unanswered     { background-color: rgba(158, 158, 158, 0.15) !important; color: #424242 !important; }
.status-chip--unpaid         { background-color: rgba(255, 87, 34, 0.15)   !important; color: #bf360c !important; }

.canceled-reason {
    margin-top: 10px;
    font-size: 13px;
    line-height: 20px;
    color: #616161;

    strong {
        font-weight: 600;
        color: #344054;
    }
}

/* ── Page sections ── */
.page-section {
    padding-top: 4px;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 700;
    color: #9e9e9e;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-bottom: 16px;
}

/* ── Hotel ── */
.hotel-name-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.hotel-edit-btn {
    flex-shrink: 0;
    opacity: 0.6;
    font-size: 11px !important;
    letter-spacing: 0.3px;

    &:hover { opacity: 1; }
}

.hotel-block {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
}

.hotel-section-img {
    width: 100%;
    height: 100%;
    min-height: 240px;
    max-height: 240px;
    background-color: #f5f5f5;
}

.hotel-name {
    font-size: 22px;
    font-weight: 700;
    color: #1f2933;
    line-height: 1.3;
}

.hotel-address {
    font-size: 14px;
    color: #616161;
    margin: 0;
}

/* ── Info labels / values ── */
.info-label {
    display: block;
    font-size: 11px;
    font-weight: 500;
    color: #757575;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.info-value {
    margin-top: 3px;
    font-size: 15px;
    font-weight: 600;
    color: #434347;
}

/* ── Fare cards ── */
.fare-card {
    border-radius: 8px;
}

.fare-card-title {
    background-color: #f9f9f9;
    font-size: 15px !important;
}

/* ── Guest items ── */
.guest-item {
    background-color: #f5f5f5;
    border-radius: 6px;
}

.guest-name {
    font-size: 14px;
    font-weight: 500;
    color: #1f2933;
}

.guest-doc {
    font-size: 13px;
    color: #616161;
}

/* ── History ── */
.container-status { padding: 0 40px 40px 0; }

/* ── Actions ── */
.actions-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
}

/* ── Lock placeholder ── */
.lock-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 280px;
    gap: 12px;
    padding: 48px 24px;
}

/* ── Loading ── */
.loadingScreen {
    display: flex;
    position: fixed;
    align-items: center;
    justify-content: center;
    background-color: whitesmoke;
    width: 100vw;
    height: 100vh;
    z-index: 50;

    h1 { padding-bottom: 7rem; }
}

.email-error {
    font-size: 14px;
    color: #f44336;
}

/* ── Responsive ── */
@media (max-width: 960px) {
    .container-detail {
        width: 100%;
        padding: 16px 12px;
    }

    .booking-detail-card { padding: 0 14px 24px; }

    .booking-header {
        margin: 0 -14px 20px;
        padding: 8px 14px 12px;
    }

    .booking-header-toolbar {
        align-items: flex-start;
        flex-direction: column;
        gap: 8px;
    }

    .assignment-toolbar-panel { width: 100%; }

    .booking-overview { gap: 16px; }
}
</style>
